import { useMemo } from "react";
import { buildLegislationPrintHeader } from "@/lib/legislationPrintHeader";
import type { Legislation } from "@/models/legislation/Legislation";

type LegislationPrintHeaderProps = {
  legislation: Legislation | null;
};

/**
 * Number and date of the source, shown only on the printed page (LAR-121).
 *
 * The on-screen viewer already carries this in the toolbar and metadata sidebar,
 * both of which are `no-print`; without it a printout of a Retsinformation law,
 * an ARVID decision, an EU act or a Domsdatabasen judgment has no way of saying
 * which one it is. `PrintStyles` keeps `.print-only` hidden on screen and
 * reveals it under `@media print`.
 */
const LegislationPrintHeader: React.FC<LegislationPrintHeaderProps> = ({
  legislation,
}) => {
  const header = useMemo(
    () => buildLegislationPrintHeader(legislation),
    [legislation]
  );

  if (!header) return null;

  return (
    <div
      className="print-only mb-4 border-b border-border pb-2 text-foreground"
      data-testid="legislation-print-header"
    >
      {header.headline && (
        <p className="text-base font-bold">{header.headline}</p>
      )}
      {header.details.length > 0 && (
        <p className="text-sm">{header.details.join(" · ")}</p>
      )}
    </div>
  );
};

export default LegislationPrintHeader;
